import { useEffect } from "react"

import { useAuthData } from "@/lib/zero"

const PREFIX = "last-known:"

function storageKey(workspaceId: string, name: string) {
	return `${PREFIX}${workspaceId}:${name}`
}

export function readLastKnown<T>(key: string): T | undefined {
	try {
		const raw = window.localStorage.getItem(key)
		return raw === null ? undefined : (JSON.parse(raw) as T)
	} catch {
		return undefined
	}
}

export function writeLastKnown(key: string, value: unknown) {
	try {
		window.localStorage.setItem(key, JSON.stringify(value))
	} catch {
		// Private mode or a full quota only costs the next cold load a jump.
	}
}

/**
 * The live value once Zero has it; until then, what this workspace showed last
 * time, so the sidebar draws in its final shape instead of shifting on load.
 */
export function useLastKnown<T>(
	name: string,
	value: T | undefined,
	fallback: T,
): T {
	const { workspaceId } = useAuthData()
	const key = storageKey(workspaceId, name)
	const serialized = value === undefined ? undefined : JSON.stringify(value)

	useEffect(() => {
		if (serialized === undefined) return
		writeLastKnown(key, JSON.parse(serialized))
	}, [key, serialized])

	if (value !== undefined) return value
	return readLastKnown<T>(key) ?? fallback
}
